import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import {Router} from '@angular/router';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from "@ngneat/transloco";
import {ORGANISER_EDIT} from 'src/app/app-routing.module';
import {AbstractComponent} from "@components";
import {
  ApplicationProcedureTypes,
  Category,
  DistanceMarkers,
  EventItem,
  FetchRaceByIdRequest,
  GenderSeparationTypes,
  Lookups,
  PenaltyMinutesTypes,
  Race,
  RaceSettings,
  RegistrationDisplayFlags,
  ResultDisplayType,
  ResultProcessingType,
  sportsTypeVisibilityMap,
  SportType,
  SportTypeFieldsVisibility,
  TeamRankingTeamSeparationTypes,
  TimingSystem,
  TeamRaceType,
  TOAST_STATE
} from "@model";
import {LoaderService, RaceService, RaceSettingsService, ToastService} from "@services";
import {required} from "@utility";
import {TeamType} from 'src/app/model/team-type';
import {take} from "rxjs";

@Component({
    selector: 'app-race-details-settings',
    templateUrl: './race-details-settings.component.html',
    styleUrls: ['./race-details-settings.component.scss'],
    standalone: false
})
export class RaceDetailsSettingsComponent extends AbstractComponent implements OnInit {
  @Input() raceSettings: RaceSettings = new RaceSettings();
  @Input() event?: EventItem;
  @Input() race?: Race;
  @Input() disableEditing = false;
  @Input() categoryList: Category[] = [];
  @Input() applicationProcedureTypes: Lookups[] = [];
  @Input() typeOfApplicationTypes: Lookups[] = [];
  @Input() teamTypes: Lookups[] = [];
  @Input() selectionOfTeamMembersTypes: Lookups[] = [];
  @Input() measurementTypes: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() startByTypeTypes: Lookups[] = [];
  @Input() resultDisplayTypes: Lookups[] = [];
  @Input() genderSeparationTypes: Lookups[] = [];
  @Input() teamRankingTeamSeparationTypes: Lookups[] = [];
  @Input() timingSystems: Lookups[] = [];
  @Input() timingEquipments: Lookups[] = [];
  @Input() penaltyMinutesTypes: Lookups[] = [];
  @Output() raceChange = new EventEmitter<Race>();

  @ViewChild('categorySelect') dropdown?: NgbDropdown;
  resultProcessingTypes : ResultProcessingType[] = [ResultProcessingType.DISTANCE_DEFINED, ResultProcessingType.ROUND_RACE, ResultProcessingType.BACKYARD_ULTRA, ResultProcessingType.TIME_LIMITED];
  teamRaceTypes : TeamRaceType[] = [TeamRaceType.RELAY, TeamRaceType.CONCURRENT];

  inputForm = new FormGroup({
    applicationProcedure: new FormControl(),
    typeOfApplication: new FormControl(),
    teamType: new FormControl(),
    teamRaceType: new FormControl(),
    selectionOfTeamMembersType: new FormControl(),
    teamNameRequired: new FormControl(),
    genderRequired: new FormControl(),
    measurementType: new FormControl(),
    measurementPrecisionType: new FormControl(),
    startByType: new FormControl(),
    resultsPerPage: new FormControl(),
    topRanked: new FormControl(),
    teamParticipantsForRanking: new FormControl(),
    teamRankingGenderSeparation: new FormControl(),
    rankingGroups: new FormControl(),
    participantsFromRankingGroup: new FormControl(),
    groupRankingSeparation: new FormControl(),
    teamRankingTeamSeparation: new FormControl(),
    penaltyMinutes: new FormControl(),
    controlPoints: new FormControl(),
    minimumLapDuration: new FormControl(),
    maxLapsCount: new FormControl(),
    rotatingOrder: new FormControl(),
    rotatingResultsSeconds: new FormControl(),
    registrationExternalUrl: new FormControl(),
    category: new FormControl(),
    maxGroupRegisteredParticipants: new FormControl(),
    useGroupRanking: new FormControl(),
    timingSystem: new FormControl(),
    timingEquipment: new FormControl(),
    enableGroupRegistrationPayment: new FormControl(),
    usingUploadRoute: new FormControl(),
    displayControlPoints: new FormControl(),
    showRouteProfile: new FormControl(),
    enableGPXDownload: new FormControl(),
    usingDistanceMarkersOnRoute: new FormControl(),
    distanceMarkersOnRoute: new FormControl(),
    maxParticipantsInStartBlock: new FormControl(),
    blockStart: new FormControl(),
    resultProcessingType: new FormControl(),
    lapLength: new FormControl(),
    resultDisplayType: new FormControl()
  });

  distanceMarkers = new Map<number, string>();

  constructor(
    public translocoService: TranslocoService,
    private raceSettingsService: RaceSettingsService,
    private toastService: ToastService,
    private router: Router,
    private loaderService: LoaderService,
    private raceService: RaceService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToValueChanges();
    this.initForm();
    this.raceSettingsChanged();
    this.inputForm.updateValueAndValidity()
    this.distanceMarkers = this.getDistanceMarkers();
  }

  private raceSettingsChanged = () => {
    this.initDefaultFields();
    if (this.raceSettings) {
      this.inputForm.patchValue(this.raceSettings);
      if (!this.raceSettings.resultProcessingType) {
        if (this.race?.sport === SportType.BACKYARD_ULTRA) {
          this.inputForm.controls.resultProcessingType.setValue(ResultProcessingType.BACKYARD_ULTRA, {onlySelf: true, emitEvent: false});
          this.inputForm.controls.resultProcessingType.disable();
        }
        else {
          if ((!this.raceSettings.minimumLapDuration || this.raceSettings.minimumLapDuration === "0")) {
            this.inputForm.controls.resultProcessingType.setValue(ResultProcessingType.DISTANCE_DEFINED, {onlySelf: true, emitEvent: false});
            this.disableField(this.inputForm.controls.minimumLapDuration, "0");
            this.disableField(this.inputForm.controls.lapLength, "0");
          } else {
            this.inputForm.controls.resultProcessingType.setValue(ResultProcessingType.ROUND_RACE, {onlySelf: true, emitEvent: false});
            this.enableField(this.inputForm.controls.minimumLapDuration, true, this.raceSettings.minimumLapDuration);
            this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
          }
        }
      }
      if (this.raceSettings.rankingGroups && this.raceSettings.useGroupRanking) {
        let rankingGroups = this.raceSettings.rankingGroups;
        if (rankingGroups.endsWith(";")) {
          rankingGroups = rankingGroups.slice(0, -1);
        }
        this.inputForm.controls.rankingGroups.setValue(rankingGroups.split(";"));
      }
      this.inputForm.controls.category.setValue(this.raceSettings.category?.id);
    }
    if (this.race?.sport) {
      if (Object.values(SportType).includes(this.race.sport)) {
        const sportTypeFieldsVisibility: SportTypeFieldsVisibility = sportsTypeVisibilityMap[this.race.sport as SportType];
        if (sportTypeFieldsVisibility.maxParticipantsInStartBlock || sportTypeFieldsVisibility.maxParticipantsInStartBlock === 0) {
          this.inputForm.controls.blockStart.enable();
        }
        else {
          this.inputForm.controls.blockStart.setValue(null);
          this.inputForm.controls.blockStart.disable();
          this.inputForm.controls.maxParticipantsInStartBlock.setValue(null);
          this.inputForm.controls.maxParticipantsInStartBlock.disable();
        }
      }
    }
    this.inputForm.updateValueAndValidity();
  }

  private initForm = () => {
    const requiredFields = [
      'applicationProcedure', 'typeOfApplication', 'teamType', 'measurementPrecisionType',
      'measurementType', 'startByType', 'resultsPerPage', 'topRanked', 'teamRankingGenderSeparation',
      'controlPoints', 'penaltyMinutes', 'minimumLapDuration', 'resultDisplayType',
      'category', 'enableGroupRegistrationPayment', 'useGroupRanking', 'timingSystem',
    ];
    requiredFields.forEach(field => (this.inputForm.get(field) as FormControl).addValidators([required]));
    if (this.disableEditing) {
      this.inputForm.disable();
    }
    this.inputForm.controls.enableGroupRegistrationPayment.addValidators([
      (control: AbstractControl): { [key: string]: any } | null => {
        const groupRegistrationEnabled = this.event?.paymentSettings?.mandatoryPayment ?? false;
        const isUsed = control.value;

        if (!groupRegistrationEnabled && isUsed) {
          control.markAsTouched();
          return { mandatoryPaymentMissing: true };
        }
        return null;
      }
    ]);
  }

  private initDefaultFields = () => {
    if (!this.raceSettings) {
      this.raceSettings = new RaceSettings();
    }
    this.raceSettings.applicationProcedure ??= ApplicationProcedureTypes.STANDARD;
    this.raceSettings.teamRankingGenderSeparation ??= TeamRankingTeamSeparationTypes.NOT_USED;
    this.raceSettings.penaltyMinutes ??= PenaltyMinutesTypes.NOT_VISIBLE;
    this.raceSettings.useGroupRanking ??= false;
    this.raceSettings.resultDisplayType ??= ResultDisplayType.STANDARD;
    this.raceSettings.enableGroupRegistrationPayment ??= false;
    if (this.raceSettings.usingUploadRoute === undefined || this.raceSettings.usingUploadRoute === null) {
        this.raceSettings.usingUploadRoute = true;
        this.raceSettings.displayControlPoints = true;
        this.raceSettings.showRouteProfile = true;
        this.raceSettings.enableGPXDownload = true;
      }
    this.raceSettings.blockStart ??= false;
  }

  private subscribeToValueChanges = () => {
    const controls = this.inputForm.controls;
    this.subscriptions.push(controls.teamType.valueChanges.subscribe(this.teamTypeChanged));
    this.subscriptions.push(controls.enableGroupRegistrationPayment.valueChanges.subscribe(this.enableGroupRegistrationPaymentChanged));
    this.subscriptions.push(controls.timingSystem.valueChanges.subscribe(this.timingSystemChanged));
    this.subscriptions.push(controls.useGroupRanking.valueChanges.subscribe(this.useGroupingChanged));
    this.subscriptions.push(controls.usingUploadRoute.valueChanges.subscribe(this.usingUploadRouteChanged));
    this.subscriptions.push(controls.usingDistanceMarkersOnRoute.valueChanges.subscribe(this.usingDistanceMarkersOnRouteChanged));
    this.subscriptions.push(controls.blockStart.valueChanges.subscribe(this.startBlockChanged));
    this.subscriptions.push(controls.resultProcessingType.valueChanges.subscribe(this.resultProcessingTypeChanged));
    this.subscriptions.push(controls.resultDisplayType.valueChanges.subscribe(this.resultDisplayTypeChanged));
    this.subscriptions.push(controls.teamRaceType.valueChanges.subscribe(this.teamRaceTypeChanged));
  }

  private usingUploadRouteChanged = (isUsing: boolean) => {
    if (isUsing) {
      this.enableField(this.inputForm.controls.showRouteProfile, false, true);
      this.enableField(this.inputForm.controls.enableGPXDownload, false, true);
      this.enableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    } else {
      this.disableField(this.inputForm.controls.showRouteProfile);
      this.disableField(this.inputForm.controls.enableGPXDownload);
      this.disableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    }
  }

  private usingDistanceMarkersOnRouteChanged = (isUsed: boolean) => {
    isUsed ? this.enableField(this.inputForm.controls.distanceMarkersOnRoute) : this.disableField(this.inputForm.controls.distanceMarkersOnRoute);
  }

  private startBlockChanged = (isUsed: boolean) => {
    if (isUsed) {
      this.enableField(this.inputForm.controls.maxParticipantsInStartBlock, true);
      this.inputForm.controls.maxParticipantsInStartBlock.addValidators(Validators.min(1));
      this.inputForm.controls.maxParticipantsInStartBlock.updateValueAndValidity();
    } else {
      this.disableField(this.inputForm.controls.maxParticipantsInStartBlock);
    }
  }

  private resultProcessingTypeChanged = (type: ResultProcessingType) => {
    switch (type) {
      case ResultProcessingType.ROUND_RACE:
        this.enableField(this.inputForm.controls.minimumLapDuration, true, "0");
        this.enableField(this.inputForm.controls.maxLapsCount, false, undefined);
        this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
        this.inputForm.controls.maxLapsCount.addValidators(Validators.min(1));
        break;
      case ResultProcessingType.TIME_LIMITED:
        this.enableField(this.inputForm.controls.minimumLapDuration, true, "0");
        this.enableField(this.inputForm.controls.lapLength, true,  "0");
        this.disableField(this.inputForm.controls.maxLapsCount);
        this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
        this.inputForm.controls.lapLength.addValidators(Validators.min(1));
        break;
      case ResultProcessingType.BACKYARD_ULTRA:
        if (this.race?.sport === SportType.BACKYARD_ULTRA) {
          this.inputForm.controls.resultProcessingType.disable();
        }
        this.disableField(this.inputForm.controls.minimumLapDuration);
        this.disableField(this.inputForm.controls.lapLength);
        this.disableField(this.inputForm.controls.maxLapsCount);
        break;
      default:
        this.disableField(this.inputForm.controls.minimumLapDuration);
        this.disableField(this.inputForm.controls.lapLength);
        this.disableField(this.inputForm.controls.maxLapsCount);
        break;
    }
  }
  private resultDisplayTypeChanged = (type: ResultDisplayType) => {
    switch (type) {
      case ResultDisplayType.ROTATING:
        this.enableField(this.inputForm.controls.rotatingResultsSeconds, true);
        this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
        break;
      default:
        this.disableField(this.inputForm.controls.rotatingResultsSeconds);
        break;
    }
  }

  private teamRaceTypeChanged = (type: TeamRaceType) => {
    switch (type) {
      case TeamRaceType.CONCURRENT:
        this.disableField(this.inputForm.controls.resultProcessingType, ResultProcessingType.DISTANCE_DEFINED);
        break;
      default:
        this.enableField(this.inputForm.controls.resultProcessingType, true);
        break;
    }
  }

  private teamTypeChanged = (teamType: number | null) => {
    const controls = this.inputForm.controls;
    if (teamType === TeamType.SINGLE || teamType === null) {
      this.disableField(controls.selectionOfTeamMembersType);
      this.disableField(controls.teamNameRequired);
      this.disableField(controls.genderRequired);
      this.disableField(controls.teamRaceType);
      this.enableField(controls.enableGroupRegistrationPayment);
      this.enableField(controls.maxGroupRegisteredParticipants);
    } else {
      this.enableField(controls.selectionOfTeamMembersType, true, RegistrationDisplayFlags.NOT_USED);
      if (this.race?.sport === SportType.BACKYARD_ULTRA) {
        this.disableField(controls.teamRaceType, TeamRaceType.RELAY);
      }
      else {
        this.enableField(controls.teamRaceType, true, TeamRaceType.RELAY);
      }
      this.enableField(controls.teamNameRequired, true, false);
      this.enableField(controls.genderRequired, true, false);
      this.disableField(controls.enableGroupRegistrationPayment);
      this.disableField(controls.maxGroupRegisteredParticipants);
    }
  }

  private enableGroupRegistrationPaymentChanged = (isUsed: boolean | null) => {
    const groupRegistrationEnabled = this.event?.paymentSettings?.mandatoryPayment ?? false;
    isUsed && groupRegistrationEnabled ? this.enableField(this.inputForm.controls.maxGroupRegisteredParticipants, true) : this.disableField(this.inputForm.controls.maxGroupRegisteredParticipants);
  }

  private timingSystemChanged = (timingSystem: number | null) => {
    timingSystem === TimingSystem.PLATFORM || timingSystem === null
      ? this.disableField(this.inputForm.controls.timingEquipment)
      : this.enableField(this.inputForm.controls.timingEquipment, true);
  }

  private useGroupingChanged = (isUsed: boolean | null) => {
    if (isUsed) {
      this.enableField(this.inputForm.controls.rankingGroups);
      this.enableField(this.inputForm.controls.participantsFromRankingGroup);
      this.enableField(this.inputForm.controls.groupRankingSeparation, true, GenderSeparationTypes.NOT_USED);
      this.enableField(this.inputForm.controls.teamRankingTeamSeparation, true, TeamRankingTeamSeparationTypes.NOT_USED);
    } else {
      this.disableField(this.inputForm.controls.rankingGroups);
      this.disableField(this.inputForm.controls.participantsFromRankingGroup);
      this.disableField(this.inputForm.controls.groupRankingSeparation);
      this.disableField(this.inputForm.controls.teamRankingTeamSeparation);
    }
  }

  private getRaceSettingsFields = (): RaceSettings => {
    this.checkValidators(this.inputForm);
    let raceSettings: RaceSettings = { ...this.raceSettings };
    if (this.inputForm.controls.rankingGroups.value && raceSettings.useGroupRanking) {
      let rankingGroups: string[] = this.inputForm.controls.rankingGroups.value;
      raceSettings.rankingGroups = rankingGroups.join(";");
    } else {
      raceSettings.rankingGroups = undefined;
    }
    if (!raceSettings.raceId) raceSettings.raceId = this.race?.id;
    const controls = this.inputForm.controls;
    raceSettings.applicationProcedure = controls.applicationProcedure.value;
    raceSettings.typeOfApplication = controls.typeOfApplication.value;
    raceSettings.teamType = controls.teamType.value;
    raceSettings.selectionOfTeamMembersType = controls.selectionOfTeamMembersType.value;
    raceSettings.teamRaceType = controls.teamRaceType.value;
    raceSettings.teamNameRequired = controls.teamNameRequired.value;
    raceSettings.genderRequired = controls.genderRequired.value;
    raceSettings.measurementType = controls.measurementType.value;
    raceSettings.measurementPrecisionType = controls.measurementPrecisionType.value;
    raceSettings.startByType = controls.startByType.value;
    raceSettings.resultsPerPage = controls.resultsPerPage.value;
    raceSettings.topRanked = controls.topRanked.value;
    raceSettings.teamParticipantsForRanking = controls.teamParticipantsForRanking.value;
    raceSettings.teamRankingGenderSeparation = controls.teamRankingGenderSeparation.value;
    raceSettings.participantsFromRankingGroup = controls.participantsFromRankingGroup.value;
    raceSettings.groupRankingSeparation = controls.groupRankingSeparation.value;
    raceSettings.teamRankingTeamSeparation = controls.teamRankingTeamSeparation.value;
    raceSettings.penaltyMinutes = controls.penaltyMinutes.value;
    raceSettings.controlPoints = controls.controlPoints.value;
    raceSettings.resultProcessingType = controls.resultProcessingType.value;
    raceSettings.resultDisplayType = controls.resultDisplayType.value;
    raceSettings.minimumLapDuration = controls.minimumLapDuration.value;
    raceSettings.lapLength = controls.lapLength.value;
    raceSettings.rotatingResultsSeconds = controls.rotatingResultsSeconds.value;
    raceSettings.registrationExternalUrl = controls.registrationExternalUrl.value;
    raceSettings.category = this.categoryList.find(category => category.id === controls.category.value);
    raceSettings.enableGroupRegistrationPayment = controls.enableGroupRegistrationPayment.value;
    raceSettings.maxGroupRegisteredParticipants = controls.maxGroupRegisteredParticipants.value;
    raceSettings.useGroupRanking = controls.useGroupRanking.value;
    raceSettings.timingSystem = controls.timingSystem.value;
    raceSettings.timingEquipment = controls.timingEquipment.value;
    raceSettings.usingUploadRoute = controls.usingUploadRoute.value;
    raceSettings.displayControlPoints = controls.displayControlPoints.value;
    raceSettings.showRouteProfile = controls.showRouteProfile.value;
    raceSettings.enableGPXDownload = controls.enableGPXDownload.value;
    raceSettings.usingDistanceMarkersOnRoute = controls.usingDistanceMarkersOnRoute.value;
    raceSettings.distanceMarkersOnRoute = controls.distanceMarkersOnRoute.value;
    raceSettings.blockStart = controls.blockStart.value;
    raceSettings.maxParticipantsInStartBlock = controls.maxParticipantsInStartBlock.value;
    raceSettings.maxLapsCount = controls.maxLapsCount.value;
    return raceSettings;
  }

  getDistanceMarkers = (): Map<number, string> => {
    const optionsMap = new Map<number, string>();
    Object.values(DistanceMarkers).forEach(distanceMarker => {
      if (typeof distanceMarker === 'number') {
        optionsMap.set(distanceMarker, this.translocoService.translate("race.settings.distanceMarkers." + distanceMarker));
      }
    });
    return optionsMap;
  }

  onSave = (): void => {
    this.markAllAsTouched(this.inputForm);
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.form"));
      return;
    }
    if (!this.isRaceSettingsValid()) {
      return;
    }
    if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    }
    const raceSettings = this.getRaceSettingsFields();
    this.loaderService.startLoading();
    this.raceSettingsService.updateRaceSettings(raceSettings, () => {
      if (this.race?.id) {
        this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(this.race.id), false)
          .pipe(take(1)).subscribe(race => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("app.common.success.race.settings.edit"));
          this.raceChange.emit(race ?? undefined);
        });
      }
    }, (errorCode: number | undefined) => {
      this.loaderService.dismissLoading();

      const errorFieldMap: Record<number, string> = {
        [RaceSettingsService.BLOCK_RACE_EDIT_NOT_ALLOWED]: "race.settings.blockStart",
        [RaceSettingsService.START_BY_TYPE_EDIT_NOT_ALLOWED]: "race.settings.startByType",
        [RaceSettingsService.RESULT_PROCESSING_TYPE_EDIT_NOT_ALLOWED]: "race.settings.resultProcessingType",
        [RaceSettingsService.LAP_LENGTH_EDIT_NOT_ALLOWED]: "race.settings.lapLength",
        [RaceSettingsService.MINIMUM_LAP_DURATION_EDIT_NOT_ALLOWED]: "race.settings.minimumLapDuration",
        [RaceSettingsService.MAX_LAPS_COUNT_EDIT_NOT_ALLOWED]: "race.settings.maxLapsCount",
        [RaceSettingsService.TEAM_RACE_TYPE_EDIT_NOT_ALLOWED]: "race.settings.teamRaceType"
      };

      const fieldKey = errorCode !== undefined ? errorFieldMap[errorCode] : undefined;
      const errorMessage = fieldKey
          ? this.translocoService.translate("race.settings.update.error", {
            field: this.translocoService.translate(fieldKey)
          })
          : this.translocoService.translate("app.common.server.error");

      this.toastService.showToast(TOAST_STATE.danger, errorMessage);
    });
  }

  onCancel = (): void => {
    this.raceSettingsChanged();
  }

  rankingGroupsChanged = (tags: string[] | undefined) => {
    this.inputForm.controls.rankingGroups.setValue(tags);
  }

  navigateToOrganiserPage = () => {
    if (this.race?.organiserId) {
      this.router.navigate([ORGANISER_EDIT], { queryParams: { organiserId: this.race.organiserId } });
    }
  }

  removeTagAtIndex = (index: number) => {
    const tags = [...this.inputForm.controls.rankingGroups.value];
    tags.splice(index, 1);
    this.inputForm.controls.rankingGroups.setValue(tags);
  }

  isRaceSettingsValid = (): boolean => {
    const isBackyardUltra: boolean = this.inputForm.controls.resultProcessingType.value === ResultProcessingType.BACKYARD_ULTRA;
    if ((RaceSettings.hasMinimumLapDuration(this.inputForm.controls.minimumLapDuration.value) || isBackyardUltra) && RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.blockStart"));
      return false;
    } else if ((RaceSettings.hasMinimumLapDuration(this.inputForm.controls.minimumLapDuration.value) || isBackyardUltra) && RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value)){
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.startByGender"));
      return false;
    } else if ((RaceSettings.hasMinimumLapDuration(this.inputForm.controls.minimumLapDuration.value) || isBackyardUltra) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.startEvery"));
      return false;
    } else if (RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value) && RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.blockRace.startByGender"));
      return false;
    } else if (RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.blockRace.startEvery"));
      return false;
    } else if (RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.startByGender.startEvery"));
      return false;
    }
    return true;
  }


  protected readonly SportType = SportType;
  protected readonly ResultProcessingType = ResultProcessingType;

}
